import React, {
  Component,
  createContext,
} from 'react';
import {
  Table,
  Popconfirm,
  Form,
  Button,
} from 'antd';
import EditableTableCell from './EditableTableCell';
import i18n from '../../config/i18n';

export const EditableContext = createContext();

class MetadataTable extends Component {
  constructor(props) {
    super(props);
    const {
      metadataKey,
    } = props;

    this.state = {
      data: this.handleMetadata(),
      editingKey: '',
    };

    this.columns = [
      {
        title: () => i18n.t('name'),
        dataIndex: 'name',
        width: '25%',
        editable: true,
        isRequired: true,
      },
      {
        title: () => i18n.t('code'),
        dataIndex: 'code',
        width: '15%',
        editable: true,
        isRequired: false,
      },
      {
        title: () => i18n.t('description'),
        dataIndex: 'description',
        width: '40%',
        editable: true,
        isRequired: false,
      },
    ];

    // Customize col of 'title' since it has additional property 'role'.
    // TODO: Modify roleId if needed.
    if (metadataKey === 'title') {
      this.columns.push(
        {
          title: () => i18n.t('role'),
          dataIndex: 'roleName',
          width: '40%',
          editable: false,
          isRequired: false,
        }
      );
    }

    this.columns.push(
      {
        title: () => i18n.t('actions'),
        dataIndex: 'actions',
        render: (text, record) => {
          const { editingKey } = this.state;
          const editable = this.isEditing(record);
          if (editable) {
            const onSaveClicked = record.key === metadataKey ? this.create : this.save;
            return (
              <span>
                <EditableContext.Consumer>
                  {form => (
                    <Button
                      type="link"
                      onClick={() => onSaveClicked(form, record)}
                    >
                      {i18n.t('save')}
                    </Button>
                  )}
                </EditableContext.Consumer>
                <Popconfirm
                  title={i18n.t('sureToCancel')}
                  onConfirm={() => this.cancel(record.key)}
                >
                  <Button type="link">
                    {i18n.t('cancel')}
                  </Button>
                </Popconfirm>
              </span>
            );
          }

          // The creation row
          if (record.key === metadataKey) {
            return (
              <span>
                <Button
                  type="link"
                  disabled={editingKey !== ''}
                  onClick={() => this.edit(record.key)}
                >
                  {i18n.t('create')}
                </Button>
              </span>
            );
          }

          // Normal rows
          // antd Popconfirm has some problems on the "disabled" property:
          // https://github.com/ant-design/ant-design/issues/14788
          return (
            <span>
              <Button
                type="link"
                disabled={editingKey !== ''}
                onClick={() => this.edit(record.key)}
              >
                {i18n.t('edit')}
              </Button>
              {editingKey !== '' ? (
                <Button
                  type="link"
                  disabled
                >
                  {i18n.t('delete')}
                </Button>
              ) : (
                <Popconfirm
                  title={i18n.t('sureToDelete')}
                  onConfirm={() => this.delete(record.id)}
                >
                  <Button
                    type="link"
                  >
                    {i18n.t('delete')}
                  </Button>
                </Popconfirm>
              )}
            </span>
          );
        },
      }
    );
  }

  handleMetadata = () => {
    const {
      metadataKey,
      metadataList,
    } = this.props;

    return metadataKey === 'title'
      ? metadataList.map((data) => {
        data.roleName = (data.role || {}).name;
        return data;
      })
      : metadataList;
  }

  isEditing = record => record.key === this.state.editingKey;

  cancel = () => {
    this.setState({ editingKey: '' });
  };

  save = (form, record) => {
    const {
      key,
      ...rest
    } = record;
    const {
      actions: {
        updateMetadata,
        createSuccessAlert,
        createDangerAlert,
        getMetadata,
      },
    } = this.props;

    form.validateFields(async (error, row) => {
      if (error) {
        return;
      }
      const newData = [...this.state.data];
      const index = newData.findIndex(item => key === item.key);

      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          ...row,
        });
        this.setState({ data: newData, editingKey: '' });
      } else {
        newData.push(row);
        this.setState({ data: newData, editingKey: '' });
      }

      try {
        await updateMetadata({
          ...rest,
          ...row,
        });
        await getMetadata();
        this.setState({ data: this.handleMetadata() });
        createSuccessAlert(i18n.t('updateSuccess'));
      } catch (err) {
        createDangerAlert(err && err.message);
      }
    });
  };

  create = (form, record) => {
    const {
      key,
      ...rest
    } = record;
    const {
      actions: {
        createMetadata,
        createSuccessAlert,
        createDangerAlert,
        getMetadata,
      },
      metadataTypeId,
    } = this.props;

    form.validateFields(async (error, row) => {
      if (error) {
        return;
      }
      const newData = [...this.state.data];

      // Set remaining fields
      row.metadataTypeId = metadataTypeId;
      newData.push(row);

      try {
        await createMetadata({
          ...rest,
          ...row,
        });
        await getMetadata();
        this.setState({
          data: this.handleMetadata(),
          editingKey: '',
        });
        createSuccessAlert(i18n.t('updateSuccess'));
      } catch (err) {
        createDangerAlert(err && err.message);
      }
    });
  };

  edit = (key) => {
    this.setState({ editingKey: key });
  };

  delete = async (id) => {
    const {
      actions: {
        deleteMetadata,
        createSuccessAlert,
        createDangerAlert,
        getMetadata,
      },
    } = this.props;
    try {
      await deleteMetadata(id);
      await getMetadata();
      this.setState({ data: this.handleMetadata() });
      createSuccessAlert(i18n.t('deleteSuccess'));
    } catch (err) {
      createDangerAlert(err && err.message);
    }
  };

  render() {
    const {
      isLoading,
      metadataKey,
    } = this.props;
    const components = {
      body: {
        cell: EditableTableCell,
      },
    };
    const columnsWithCustCell = this.columns.map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: record => ({
          record,
          inputType: 'text',
          dataIndex: col.dataIndex,
          title: col.title,
          required: col.isRequired,
          editing: this.isEditing(record),
        }),
      };
    });
    // Append empty row as creation form
    const dataSourceWithCreation = [
      ...this.state.data,
      {
        key: metadataKey,
        name: '',
        code: '',
        description: '',
      },
    ];

    return (
      <EditableContext.Provider value={this.props.form}>
        <Table
          components={components}
          dataSource={dataSourceWithCreation}
          columns={columnsWithCustCell}
          rowClassName="editable-row"
          pagination={{
            onChange: this.cancel,
          }}
          loading={isLoading}
        />
      </EditableContext.Provider>
    );
  }
}

const EditableMetadataFormTable = Form.create()(MetadataTable);

export default EditableMetadataFormTable;



// WEBPACK FOOTER //
// ./src/components/metadata/MetadataTable.js